import axios from "axios";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import BaseService from "./BaseService";
import AuthStorage from "../storage/AuthStorage";
import LogManager from "./LogManager";
import Config from "./Config";
import UrlService from "./UrlService";
import qs from "querystring";
import RefreshTimer from "../data/RefreshTimer";

/**
 * Datos del cliente OAuth2, resueltos desde la configuracion.
 */
export interface ConsumerData {
    rest_url: string;
    oauth2_client_id: string;
    oauth2_scope: string;
    redirect_url: string;
    /** Solo en clientes confidenciales. @see AppConfig */
    oauth2_client_secret?: string;
}

/**
 * Tokens tal y como los devuelve /oauth2/token, y como se guardan en storage.
 */
export interface Oauth2Tokens {
    access_token: string;
    refresh_token?: string;
    scope?: string;
    token_type: string;
    /** Segundos hasta la caducidad. Alimenta el refreshTimer. */
    expires_in?: number;
}

/**
 * Cuerpo de error que devuelve el servidor OAuth2.
 */
export interface Oauth2ErrorData {
    error?: string;
    error_description?: string;
}

class Oauth2Service extends BaseService {

    /**
     * auth types
     */
    static IMPLICIT_REQUEST_TYPE = 'authorization_code';
    static REFRESH_REQUEST_TYPE = 'refresh_token';
    static PASSWORD_REQUEST_TYPE = 'password';
    static CLIENT_CREDENTIALS_REQUEST_TYPE = 'client_credentials';

    /*
     * auth type used in this installation
     */
    static requestType: string;

    /**
     * Define storage class
    */
    static storage = AuthStorage;

    /**
     * storage oauth2 key
    */
    static STORAGE_OAUTH2_KEY = 'oauth2_tokens';
    static STORAGE_OAUTH2_STATE = 'oauth2_State';

    /**
    * response error message used to detect if we need to refresh token
     */
    static EXPIRED_TOKEN_MESSAGE = 'The access token provided has expired';

    static refreshTimer = new RefreshTimer('oauth_refresh_timer');

    /**
     * Gets consumer data.
     */
    static getConsumerData(): ConsumerData {
        const config = this.config();

        if(!config || !config.oauth2_client_id) throw new Error('Missing config: oauth_client_id');

        return  {
            rest_url: config.rest_url,
            oauth2_client_id: config.oauth2_client_id,
            oauth2_scope: config.oauth2_scope,
            redirect_url: UrlService.getCallbackUrl(),
            oauth2_client_secret: config.oauth2_client_secret,
        }
    }

    /**
     * Post request
     * @param url Absolute or relative URL (path)
     * @param config Axios config.
     */
    static async post(url: string, params: Record<string, string> = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse> {
      config.baseURL = Config.getRestUrl();

      // About 'Content-Type':
      // If application/x-www-form-urlencoded, params need to be urlencoded.
      // If application/json, then the request would take an extra CORS preflight.
      /* eslint-disable */
      if (!config.hasOwnProperty('headers')) {
        config.headers = {};
      }
      config.headers!['Content-Type'] = 'application/x-www-form-urlencoded';
      // If params are urlencoded...
      // @see https://axios-http.com/docs/urlencoded
      const data = new URLSearchParams(params);

      return axios.post(url, data, config);
   }

    /**
     * Detect if we need refresh token and if needed gets new token and returns result
     *
     * Expired tokens:
     *
     * - status: 401
     * - error: invalid_token
     * - error_description: "The access token provided has expired"
     *
     * - status: 400
     * - error: invalid_grant
     * - error_description: "Refresh token has expired"
     *
     * Invalid token:
     * - status: 400
     * - error: invalid_grant
     * - error_descpription: "refresh_token doesn't exist or is invalid for the client"
     *
     * @param response Respuesta de error.
     * @param signal Para abortar la renovación del token, si la hay.
     *
     * @return Resolves to TRUE if the token is refreshed
     */
    static async processAuthErrorResponse(response: AxiosResponse<Oauth2ErrorData>, signal?: AbortSignal): Promise<boolean> {
        this.debug("Processing Auth Error", response);

        if (response.status === 401 && response.data.error_description === this.EXPIRED_TOKEN_MESSAGE) {
            // Just return true or false, no complex errors.
            return this.requestRefreshToken(signal).then(() => true, () => false);
        } else {
            // Authentication cannot be recovered, the caller will handle it.
            this.reset();
            // @todo No authentication, but we don't want to trigger a page reload..
            // UrlService.redirectBaseUrl();
            return false;
        }
    }

    /**
     * Gets token when login.
     *
     * Possible errors:
     *
     * - status 401
     * - error: 'invalid_grant'
     * - error_description:"Invalid username and password combination"
     *
     * @param requestType PASSWORD_REQUEST_TYPE, IMPLICIT_REQUEST_TYPE or REFRESH_REQUEST_TYPE
     * @param username user name
     * @param password user password
     * @param authcode used only on implicit flow second step
     * @param signal Para abortar la petición.
     *
     * @return returns result of resending request
     */
    static async requestAccessToken(
        requestType: string,
        username?: string | null,
        password?: string | null,
        authcode?: string | null,
        signal?: AbortSignal,
    ): Promise<boolean> {
        this.debug("requestAccessToken", {requestType, username, password});
        const url = '/oauth2/token';
        const config = this.getConsumerData();

        this.requestType = requestType;

        // Confidential clients (native builds use "maps_app") must authenticate
        // on the token endpoint with a secret for every grant type
        // ("invalid_client ... must authenticate using a client secret"
        // otherwise). Public clients (web builds use "maps_gronze") have no
        // secret configured, so only send it when present — otherwise
        // URLSearchParams serializes the missing value as the literal
        // "undefined" and the server rejects the request.
        const params: Record<string, string> = {
            grant_type: requestType,
            client_id: config.oauth2_client_id,
        };
        if (config.oauth2_client_secret) {
            params.client_secret = config.oauth2_client_secret;
        }
        if(requestType === this.PASSWORD_REQUEST_TYPE) {
            params.username = username as string;
            params.password = password as string;
        } else if (requestType !== this.CLIENT_CREDENTIALS_REQUEST_TYPE) {
            params.redirect_uri = config.redirect_url;
            // Las aserciones de arriba y esta conservan el comportamiento
            // anterior: si el llamante no pasa el valor, URLSearchParams lo
            // serializa como "undefined" / "null" en vez de omitirlo.
            params.code = authcode as string;
        }

        return this.post(url, params, { signal })
          .then((response) => {
             this.setAuth2Data(response.data);
             /*
             if(requestType === this.IMPLICIT_REQUEST_TYPE) {
                 UrlService.redirectBaseUrl();
             }
             */
             return true;
          })
          .catch((error) => {
              if (error.response) {
                  // The request was made and the server responded with a status code
                  // that falls out of the range of 2xx
                  this.debug("Auth Error response", error.response);

                  // Handle known errors
                  if (error.response.status === 401 && error.response.data.error === 'invalid_grant') {
                    return Promise.reject({type: 'user', message: error.response.data.error_description, error});
                  }
              }
              // Something happened in setting up the request that triggered an Error
              this.debug('Oauth Error', error);
              return Promise.reject(LogManager.processRequestError(error));

          });
    }

    /**
     * refresh token, resend request and returns result
     *
     * @param signal Para abortar la petición.
     *
     * @return returns result of resending request
     */
    static async requestRefreshToken(signal?: AbortSignal): Promise<boolean> {
        if (this.requestType === this.CLIENT_CREDENTIALS_REQUEST_TYPE) {
            return this.requestAccessToken(this.CLIENT_CREDENTIALS_REQUEST_TYPE, null, null, null, signal);
        } else {
            const config = this.getConsumerData();
            const url = '/oauth2/token';
            const oauth = this.getAuth2Data();

            this.debug("Refreshing OAuth token", oauth);

            // Same client authentication requirement as requestAccessToken:
            // only send the secret for confidential clients that have one.
            const params: Record<string, string> = {
                grant_type: this.REFRESH_REQUEST_TYPE,
                refresh_token: oauth?.refresh_token as string,
                client_id: config.oauth2_client_id,
            }
            if (config.oauth2_client_secret) {
                params.client_secret = config.oauth2_client_secret;
            }

            return this.post(url, params, { signal })
                .then((response) => {
                    this.setAuth2Data(response.data);
                    return true;
                })
                .catch((error) => {
                    if (error.response) {
                        // The request was made and the server responded with a status code
                        // that falls out of the range of 2xx
                        this.debug("Error response " + error.response.status, error.response);
                        // Full auth reset if invalid grant.
                        if (error.response.status === 400 && error.response.data.error === 'invalid_grant') {
                            this.reset();
                        }
                        return Promise.reject(error.response);
                    } else {
                        // Something happened in setting up the request that triggered an Error
                        this.debug('Oauth Error', error);
                        return Promise.reject({
                            status: 500,
                            message: error.request,
                        });
                    }
                });
        }
    }

    /**
     * Gets / recreates authorization state code.
     *
     * @param create Create new one or use existing.
     */
    static getAuthorizationCode(create = false): string | null {
      let state;

      if (create) {
        state = Math.random().toString(36).slice(2, 7);
        this.storage.setItem(this.STORAGE_OAUTH2_STATE, state);
      }
      else {
        state = this.storage.getItem(this.STORAGE_OAUTH2_STATE);
      }
      return state;
    }

    /**
     * Gets query string parameters for Authorization request.
     * If user is not registered uses the register url and if is IOS other
     *
     * @param with_registration True to get registration URL.
     *
     * @return Full URL with query string parameters.
     */
    static getAuthorizationUrl(with_registration = false): string {
      const config = this.getConsumerData();
      const path = with_registration ? '/oauth2/register' : '/oauth2/authorize';

      return Config.getRestUrl() + path + '?' + qs.stringify({
          response_type: 'code',
          client_id: config.oauth2_client_id,
          redirect_uri: config.redirect_url,
          scope: config.oauth2_scope,
          state: this.getAuthorizationCode(true),
      });
    }

    /**
     * gets auth code forcing reload to authorize url
     * this method creates and stores state param to verify the auth response
     */
    static async requestAuthorization(with_registration = false): Promise<void> {
        this.debug("requestAuthorization");

        const uri = this.getAuthorizationUrl(with_registration);

        // redirect the user to the authorize uri
        await UrlService.redirectToUrl(uri);
    }

    /**
     * True if we are on second step implicit oauth2 flow and gets right code and state
     * @todo Remove, not used.
     */
    static checkAuthCode(): void {
        const url = window.location.href;
        const urlObject = new URL(url);
        this.extractAuthCode(urlObject.searchParams);
    }

    /**
     * Extract auth code from URL params
     *
     * @return True if we are on second step implicit oauth2 flow and gets right code and state
     */
    static async extractAuthCode(urlParams: URLSearchParams): Promise<boolean> {
        if (urlParams.has("code") && urlParams.get("state") === this.getAuthorizationCode()) {
          return this.requestAccessToken(this.IMPLICIT_REQUEST_TYPE, null, null, urlParams.get("code"));
        }
        else {
          return false;
        }
    }

    /**
     * Gets stored authorization data.
     */
    static getAuth2Data(): Oauth2Tokens | null {
        return this.storage.getObject<Oauth2Tokens>(this.STORAGE_OAUTH2_KEY);
    }

    /**
     * Gets Authorization header.
     *
     * @param check_expired Check for expiration, defaults to true
     * @param signal Para abortar la renovación del token, si hace falta.
     *
     * @return Authorization header, empty if not available.
     */
    static async getAuthorization(check_expired = true, signal?: AbortSignal): Promise<string> {
        const oauth2_headers = this.getAuth2Data();

        if (!oauth2_headers) {
          return '';
        } else if (check_expired && this.refreshTimer.needsRefresh()) {
          this.debug("getAuthorization: Token needs refreshing.");
          return this.requestRefreshToken(signal).then(
            () => { return this.getAuthorization(false, signal); },
            () => ''
          )
        }
        else {
          return oauth2_headers ? oauth2_headers.token_type + ' ' + oauth2_headers.access_token :  '';
        }
    }



    /**
     * Set / delete stored authorization data.
     *
     * @param authData Data to be stored or null for deleting it
     */
    static setAuth2Data(authData: Oauth2Tokens | null): void {
        this.debug("setAuth2Data", authData);
        if (authData) {
            this.storage.setObject(this.STORAGE_OAUTH2_KEY, authData);
            if (authData.expires_in) {
              this.refreshTimer
                .setRefreshNext(authData.expires_in)
                .saveTo(this.storage);
            }
        } else {
            this.storage.removeItem(this.STORAGE_OAUTH2_KEY);
            this.storage.removeItem(this.refreshTimer.name);
        }

    }

    /**
     * Checks whether we've got an access token.
     */
    static hasAccessToken(check_expired = true): boolean {
        const auth_data = this.getAuth2Data();

        return !!(auth_data && auth_data.access_token && (!check_expired || !this.refreshTimer.needsRefresh()));
    }

    /**
     * Reset all authentication.
     */
    static reset(): void {
        this.setAuth2Data(null);
    }

    /**
     * Initializae, load timer from storage if any...
     */
    static init(): void {
      this.refreshTimer.loadFrom(this.storage);
      this.refreshTimer.refreshLast && this.debug("Loaded Oauth refresh timer.", this.refreshTimer);
    }
}

export default Oauth2Service;
